import { DateTime } from "luxon";

/**
 * How many of a walk's new items get their content fetched before the pass ends. A tab that slept through a busy
 * stretch can wake into thousands of rows; the newest ones are what the user sees first, and the rest keep
 * `loadedContent` false so the feed view fetches them as they scroll in.
 */
export const SYNC_EAGER_CONTENT_LIMIT = 200;

/** The ids of the newest live items, at most `limit` of them; soft-deleted rows have no content worth fetching. */
export function selectEagerContentItemIds(
  items: readonly { id: string; createdAt: string; deletedAt: string | null }[],
  limit: number = SYNC_EAGER_CONTENT_LIMIT,
): string[] {
  return items
    .filter((item) => item.deletedAt == null)
    .map((item) => ({ id: item.id, createdAt: DateTime.fromJSDate(new Date(item.createdAt)) }))
    .sort((a, b) => newestFirst(a.createdAt, b.createdAt))
    .slice(0, limit)
    .map((item) => item.id);
}

/**
 * Splits the items a content pass names into the ones that need every artifact and the ones that only need what
 * changed since the content cursor.
 *
 * The content cursor means "every content signal up to this instant has been acted on", which only holds for items
 * whose artifacts were fetched whole at least once. An item still flagged unloaded — beyond the eager cap, or evicted —
 * has to start with a full fetch, or a delta would return only its newest artifacts and the flag would then claim the
 * rest.
 */
export function partitionContentFetch(
  itemIds: Iterable<string>,
  hasLoadedContent: (itemId: string) => boolean,
): { full: string[]; delta: string[] } {
  const full: string[] = [];
  const delta: string[] = [];
  for (const itemId of new Set(itemIds)) {
    (hasLoadedContent(itemId) ? delta : full).push(itemId);
  }
  return { full, delta };
}

/**
 * Orders instants newest first; one that cannot be read keeps its place.
 */
function newestFirst(a: DateTime, b: DateTime): number {
  if (a > b) {
    return -1;
  }
  if (a < b) {
    return 1;
  }
  return 0;
}
